import { HelpHero } from 'helphero';
import { DbKey } from '../adl-gen/common/db';
import * as DB from '../adl-gen/totalinteraction/futurefit/db';
import * as ST from '../admin/stores';
import { IsLoading, LoadingError } from '../loader/loader';
import { LoggedIn, LoggedInSuper, NotLoggedIn, Unregistered } from './identity-store';
import { ManagerPortalStore } from './manager-portal-store';
import { ManagerQuizResultsStore } from './manager-quiz-results-store';
import { PlotKeyToggleState } from './plot-key-toggle-state';
import { QuizResultsStore } from './quiz-results-store';
import { QuizStore } from './quiz-store';
import { UserQuizDataStore } from './user-quiz-data-store';

/** Top level app state */
export type AppState = {
    kind: 'identity-loading';
    identity: IsLoading;
} | {
    kind: 'identity-not-logged-in';
    identity: NotLoggedIn;
} | {
    kind: 'identity-unregistered';
    identity: Unregistered;
} | {
    kind: 'identity-error';
    identity: LoadingError;
} | LoggedInAppState | AdminAppState;

export type LoggedInAppState = {
    kind: 'my-assessments';
    identity: LoggedIn;
    userQuizDataStore: UserQuizDataStore;
    walkThroughFn(page: string): void;
} | {
    kind: 'quiz';
    quizStore: QuizStore;
    identity: LoggedIn;
} | {
    kind: 'self-assessment';
    quizStore: QuizStore;
    identity: LoggedIn;
} | {
    kind: 'quiz-results';
    page: 'profile' | 'guidance' | 'recommendations' | 'print';
    quizResultsStore: QuizResultsStore;
    plotKeyToggleState: PlotKeyToggleState;
    identity: LoggedIn;
    walkThroughFn(page: string): void;
    walkThroughMgr: HelpHero;
} | {
    kind: 'manager';
    identity: LoggedIn;
    managerPortalStore: ManagerPortalStore;
} | {
    kind: 'manager-quiz-results';
    page: 'profile' | 'guidance' | 'recommendations' | 'print';
    identity: LoggedIn;
    plotKeyToggleState: PlotKeyToggleState;
    managerQuizResultsStore: ManagerQuizResultsStore;
    walkThroughMgr: HelpHero;
    walkThroughFn(page: string): void;
};

export interface AdminAppState {
    kind: 'admin';
    identity: LoggedIn | LoggedInSuper;
    state: AdminState;
}
// TODO: replace with an enum?
// Basic: new/update/undefined; role: clone; user: upload/upload-results
type View = 'new' | 'update' | 'clone' | 'upload' | 'upload-results' | undefined; // TODO: "delete"

interface AdminStateUser {
    page: 'user';
    view?: View;
    appClientId: DbKey<DB.AppClient>;
    id?: DbKey<DB.AppUser> | DbKey<DB.UserUploadSet>;
    store: ST.AppUserStore | ST.AppUserUploadStore;
}

export interface AdminStateClient {
    page: 'client';
    view?: View;
    appClientId?: DbKey<DB.AppClient>;
    id?: DbKey<DB.AppClient>;
    store: ST.AppClientStore;
}

export type AdminState = {
    page: 'clients';
    view?: View;
    appClientId?: DbKey<DB.AppClient>;
    id?: DbKey<DB.AppClient>;
    store: ST.AppClientStore;
} | AdminStateClient | {
    page: 'users';
    view?: View;
    appClientId: DbKey<DB.AppClient>;
    id?: DbKey<DB.AppUser>;
    store: ST.AppUserStore;
} | AdminStateUser | {
    page: 'groups';
    view?: View;
    appClientId: DbKey<DB.AppClient>;
    id?: DbKey<DB.UserGroup>;
    store: ST.UserGroupStore;
} | {
    page: 'group';
    view?: View;
    appClientId: DbKey<DB.AppClient>;
    id?: DbKey<DB.UserGroup>;
    store: ST.UserGroupStore;
} | {
    page: 'competencies-skills';
    view?: View;
    /// TODO: Unused (yet) - and as state is not unpacked or switched in app-pagelayout,
    /// TODO: this means that appClientId has to be there
    appClientId?: DbKey<DB.AppClient>;
    id?: DbKey<DB.Competency>;
    store: ST.CompetencyStore;
} | {
    page: 'competency';
    view?: View;
    /// TODO: Unused (yet)
    appClientId?: DbKey<DB.AppClient>;
    id?: DbKey<DB.Competency>;
    store: ST.CompetencyStore;
} | {
    page: 'skill';
    view?: View;
    /// TODO: Unused (yet)
    appClientId?: DbKey<DB.AppClient>;
    id?: DbKey<DB.Skill>;
    store: ST.SkillStore;
} | {
    page: 'roles' | 'role-builder';
    view?: View;
    appClientId?: DbKey<DB.AppClient>;
    id?: DbKey<DB.WorkRole>;
    store: ST.WorkRoleStore;
} | {
    page: 'role';
    view?: View;
    appClientId?: DbKey<DB.AppClient>;
    id?: DbKey<DB.WorkRole>;
    store: ST.WorkRoleStore;
} | {
    page: 'quiz-admin';
    view?: View;
    /// TODO: Unused (yet)
    appClientId?: DbKey<DB.AppClient>;
    id?: DbKey<DB.Quiz>;
    workRoleId?: DbKey<DB.WorkRole>;
    store: ST.QuizAdminStore;
} | {
    page: 'reports';
    view: undefined;
    appClientId?: DbKey<DB.AppClient>;
    id: undefined;
    store: ST.ReportPortalStore;
} | {
    page: 'questions';
    view?: View;
    appClientId: undefined;
    id?: DbKey<DB.Question>;
    store: ST.QuestionStore;
} | {
    page: 'question';
    view?: View;
    appClientId: undefined;
    id?: DbKey<DB.Question>;
    skillId?: DbKey<DB.Skill>;
    store: ST.QuestionStore;
} | {
    page: 'recommendations';
    view?: View;
    appClientId: undefined;
    id?: DbKey<DB.TrainingOffering>;
    store: ST.TrainingOfferingStore;
} | {
    page: 'training-offering';
    view?: View;
    appClientId: undefined;
    id?: DbKey<DB.TrainingOffering>;
    // skillId?: DbKey<DB.Skill>;
    store: ST.TrainingOfferingStore;
};

interface WalkThrough {
    page: string;
    id: DB.Internationalized<string>;
    title: string;
}

interface WalkThroughMap {
    kind: string;
    walkthroughs: WalkThrough[];
}

export type WalkthroughType = 'participant-root' | 'manager-root' | 'my-assessments' | 'quiz-results' | 'manager-quiz-results';

export const walkthroughs: WalkThroughMap[] = [
    {
        kind: 'participant-root',
        walkthroughs: [
            {
                page: '',
                title: 'Participant Root',
                id: DB.makeInternationalized({ en: 'g3uwTPw17SE', zh: 'z4K9CZlqgHe', ja: '7W021dAREJ6', ko: 'cq232iORNIh' })
            }
        ]
    },
    {
        kind: 'manager-root',
        walkthroughs: [
            {
                /// TODO internationalize
                page: '',
                title: 'Manager Root',
                id: DB.makeInternationalized({ en: '2qDT0ERHMmR', zh: null, ja: null, ko: null })
            }
        ]
    },
    // {
    //   kind: "my-assessments",
    //   walkthroughs: [
    //     {
    //       page: "", title: "Participant Assessments", id: DB.makeInternationalized({ en: "ZClihu52Buo", ja: "Gb0nSjmoR8s", zh: "45TMRodqMDX", ko: "HazAzpSlxgE" })
    //     }
    //   ]
    // },
    {
        kind: 'quiz-results',
        walkthroughs: [
            {
                page: 'profile',
                title: 'Participant Profile',
                id: DB.makeInternationalized({ en: 'ahr6NRSPYvZ', ja: 'jjDOHbFRbPK', zh: 'WBEgX00IINp', ko: 'Zlwme1xSht4' })
            },
            {
                page: 'guidance',
                title: 'Participant Guidance',
                id: DB.makeInternationalized({ en: 'JhJxHeMdcRC', ja: 'GacKqlpidMl', zh: 'XGSTGDymAa0', ko: 'ESq3pES4aTS' })
            },
            {
                page: 'recommendations',
                title: 'Participant Recommendations',
                id: DB.makeInternationalized({ en: 'mwm8g9eeQQh', ja: '6D8fmTSALW7', zh: 'Ik8H5uXNtpI', ko: 'HHfN69NItZm' })
            }
        ]
    },
    {
        kind: 'manager-quiz-results',
        walkthroughs: [
            {
                page: 'profile',
                title: 'Manager Profile',
                id: DB.makeInternationalized({ en: 'vJ4rsD9rQHp', ja: 'APQ2LBoVhG7', zh: '1BUAfSLlue3', ko: 'dCj22cRNLTP' })
            },
            {
                page: 'guidance',
                title: 'Manager Guidance',
                id: DB.makeInternationalized({ en: 'xxCFLFjLdBn', ja: 'TNSwTh8ZLC1', zh: 'B1nRWZqF5B7', ko: 'mjHewHJhuTJ' })
            },
            {
                page: 'recommendations',
                title: 'Manager Recommendations',
                id: DB.makeInternationalized({ en: 'rSPoZdvSJAB', ja: '8OPd9mbZraC', zh: 'lNoA2eQCX3B', ko: '9ASnb9zoQpi' })
            }
        ]
    }
];



// WEBPACK FOOTER //
// ./src/stores/app-types.ts